/* eslint-disable prettier/prettier */
import React from 'react';
import styles from '../../styles/components/controlVariations/Pagination.module.css';

export interface PaginationProps {
  /** Current active page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback fired when a page is selected */
  onPageChange: (page: number) => void;
  /** Total number of items (optional) */
  totalItems?: number;
  /** Number of items per page (optional) */
  pageSize?: number;
  /** Custom summary text string (e.g., "Showing 5 of 15") */
  summaryText?: string;
  /** Max visible page buttons before rendering ellipses */
  maxVisiblePages?: number;
  /** Custom root CSS class name */
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  summaryText,
  maxVisiblePages = 5,
  className = '',
}) => {
  // Generate page numbers array with ellipses for large page counts
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const half = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - half);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Determine summary text content
  const getSummary = (): string | null => {
    if (summaryText) return summaryText;

    if (pageSize && totalItems) {
      const currentShowing = Math.min(
        pageSize,
        Math.max(0, totalItems - (currentPage - 1) * pageSize)
      );
      return `Showing ${currentShowing} of ${totalItems}`;
    }

    if (totalItems) {
      return `Showing ${totalItems} items`;
    }

    return null;
  };

  const calculatedSummary = getSummary();

  return (
    <nav
      className={`${styles.paginationContainer} ${className}`}
      aria-label="Pagination Navigation"
    >
      <div className={styles.pageList}>
        {getPageNumbers().map((page, idx) => {
          if (typeof page === 'string') {
            return (
              <span key={`ellipsis-${idx}`} className={styles.ellipsis}>
                {page}
              </span>
            );
          }

          const isActive = page === currentPage;

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`${styles.pageButton} ${isActive ? styles.active : ''}`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={`Go to page ${page}`}
            >
              <span className={styles.pageNumber}>{page}</span>
              {isActive && <span className={styles.activeIndicator} />}
            </button>
          );
        })}
      </div>

      {calculatedSummary && (
        <div className={styles.summaryText}>{calculatedSummary}</div>
      )}
    </nav>
  );
};